import { FC, ReactNode, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  selectDismissableModal,
  selectIsMemecoins,
  selectPageBackground,
  selectShowDashboardHeaderGradient,
  selectShowHeaderGradient,
} from '../../modules/miscUi/selectors'

import Header from '../Header'

import { useWindowSize } from '../../hooks/useWindowSize'
import { MaxWidth, PageElement } from './styles'
import Footer from '../Footer'
import {
  setIsDrops,
  setIsLaunch,
  setIsMemecoins,
} from '../../modules/miscUi/actions'
import { Link, useLocation } from 'react-router-dom'
import {
  BACK_BUTTON_PATHS,
  BACK_BUTTON_PATH_REGEX,
  FULL_WIDTH_PAGES,
  LABS_PATH_NAMES,
} from '../../App'
import { Box } from '../UI/Box'
import styled from 'styled-components'
import { useHistorianNavigation } from '../../router/historian'
import {
  FULL_SCREEN_PAGE_MAX_WIDTH,
  MYZAP_PAGE_MAX_WIDTH,
  STANDARD_PAGE_MAX_WIDTH,
  useShouldShowForThreshold,
} from '../../theme'
import { DeploymentWarning } from '../Header/styles'
import { P, Strong } from '../Library/Typeography'
import routes from '../../router/routes'
import useIsLoggedIn from '../../hooks/useIsLoggedIn'
import { BottomMenu } from '../BottomMenu'
import { ThemedButton } from '../ThemedButton'
import DismissableModal from '../Library/DismissableModal'
import { selectPreviousSeasonFocusingOn } from '../../modules/seasons/selectors'

interface Props {
  children: ReactNode
}

export const HIDE_BACKBUTTON_ROUTES = [
  '/launch/',
  '/launch',
  '/drops/',
  '/drops',
  '/vault',
]

export const isDeploying = false //false //true;

const Page: FC<Props> = ({ children }) => {
  const [menuOpen, setMenuOpen] = useState<boolean>(false)
  const { isLoggedIn } = useIsLoggedIn()

  const { width: screenWidth } = useWindowSize()
  const isAtLeastMediumDisplaySize = useShouldShowForThreshold('compact')
  const isAtLeastExpandedDisplaySize = useShouldShowForThreshold('medium')

  const pageBackground = useSelector(selectPageBackground)

  const dispatch = useDispatch()
  const { pathname } = useLocation()

  useEffect(() => {
    const pathRoute = pathname.split('/')[1]
    if (LABS_PATH_NAMES.includes(pathRoute) || !pathRoute) {
      dispatch(setIsLaunch(false))
      dispatch(setIsDrops(false))
      dispatch(setIsMemecoins(false))
    } else if (pathRoute.includes('launch')) {
      dispatch(setIsMemecoins(true))
    }
  }, [pathname, dispatch])

  const closeMenuIfOpen = () => menuOpen && setMenuOpen(false)

  useEffect(() => {
    if (screenWidth < 1000 || !menuOpen) return
    setMenuOpen(false)
  }, [screenWidth, menuOpen])

  const showHeaderGradient = useSelector(selectShowHeaderGradient)
  const showDashboardHeaderGradient = useSelector(
    selectShowDashboardHeaderGradient,
  )

  const maxWidth = usePageMaxWidth()

  const showLaunchButton = useMemo(
    () => ['/launch', '/', '/launch/'].includes(pathname),
    [pathname],
  )

  const isBackButtonPath = useMemo(
    () =>
      BACK_BUTTON_PATHS.some((path) => pathname.includes(path)) ||
      BACK_BUTTON_PATH_REGEX.some((regex) => regex.test(pathname)),
    [pathname],
  )

  const reviewingPreviousSeason = !!useSelector(selectPreviousSeasonFocusingOn)

  const showBottomMenu =
    (!isAtLeastMediumDisplaySize && reviewingPreviousSeason) ||
    (isAtLeastMediumDisplaySize && !isAtLeastExpandedDisplaySize) ||
    (!isBackButtonPath && !isAtLeastMediumDisplaySize)

  const dismissableModal = useSelector(selectDismissableModal)

  return (
    <PageElement pageBackground={pageBackground}>
      {showHeaderGradient && <div className="header-gradient" />}
      {showDashboardHeaderGradient && (
        <div className="dashboard-header-gradient" />
      )}
      {isDeploying && (
        <DeploymentWarning pad={24} ac g={8}>
          <Strong s="M" fw="bold" c="text-invert" ls="-0.28px">
            Tokens rewards are now live.{' '}
          </Strong>
          {isLoggedIn && (
            <Strong s="M" fw="bold" c="text-invert" ls="-0.28px">
              <Link
                to={routes.myzap.tokensAndVaults}
                style={{ textDecoration: 'underline' }}
              >
                Claim now
              </Link>
            </Strong>
          )}
        </DeploymentWarning>
      )}
      <Header maxWidth={maxWidth} />
      <MaxWidth maxWidth={maxWidth} onClick={closeMenuIfOpen}>
        {children}
      </MaxWidth>
      {showLaunchButton && (
        <LaunchButton>
          <Link to={routes.memecoins.launcher}>
            <ThemedButton $styleType={'primary'} w="auto">
              <img src="/img/icons/rocket.svg" alt="rocket" />
              <P s="L" fw="semibold">
                Launch a token
              </P>
            </ThemedButton>
          </Link>
        </LaunchButton>
      )}
      {showBottomMenu && <BottomMenu />}
      {!!dismissableModal && <DismissableModal />}
      <Footer />
    </PageElement>
  )
}

const LaunchButton = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  position: fixed;
  bottom: 86px;
  left: 50%;
  transform: translateX(-50%);

  @media screen and (min-width: ${({ theme }) =>
      theme.layout.expanded.indexGrids.minWidth}px) {
    bottom: 44px;
  }
`

export const BoxLeft = styled(Box)`
  flex-shrink: 0;
`

export default Page

export { MaxWidth, TextAndIcon, WarningText, Icon } from './styles'

export function usePageMaxWidth() {
  const { pathname } = useLocation()
  const pathNameWithoutTrailingSlash = pathname.replace(/\/$/, '')
  const isFullWidthPage =
    pathNameWithoutTrailingSlash === '' ||
    FULL_WIDTH_PAGES.includes(pathNameWithoutTrailingSlash)

  const isMyZapPage = pathname.includes('/myzap')
  if (isMyZapPage) {
    return MYZAP_PAGE_MAX_WIDTH
  }

  return isFullWidthPage ? FULL_SCREEN_PAGE_MAX_WIDTH : STANDARD_PAGE_MAX_WIDTH
}

export function BackButton({ padT }: { padT?: number }) {
  const { goBack } = useHistorianNavigation()
  return <PureBackButton onClick={goBack} padT={padT} />
}

export function PureBackButton({
  onClick,
  padT = 32,
}: {
  onClick: () => void
  padT?: number
}) {
  return (
    <BoxLeft g={32} padT={padT} ac fu aw>
      <Box
        pad={8}
        br={50}
        z={32}
        bst="alpha"
        bss="enabled"
        onClick={onClick}
        cp
        style={{ border: '1px solid var(--stroke-secondary, #333)' }}
      >
        <img src="/img/icons/back-arrow.svg" alt="back" />
      </Box>
    </BoxLeft>
  )
}
